import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { User } from '../../shared/models/user.models';
import { ApiService } from '../http/api.service';

import { API_URL } from 'src/app/shared/service/api.constant';
import { UserDetail } from 'src/app/store/auth/auth.model';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PasswordExpiredModalComponent } from 'src/app/shared/components/password-expired-modal/password-expired-modal.component';
import { SpinnerService } from '../services/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<User>;

  constructor(
    private http: ApiService,
    private router: Router,
    private spinner: SpinnerService,
    public modalService: NgbModal,
  ) {
    this.currentUserSubject = new BehaviorSubject(
      this.currentUserValue,
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): string {
    return localStorage.getItem('currentUser');
  }

  public get isSetPassword(): boolean {
    return localStorage.getItem('is_set_password') === 'true';
  }

  public get isPasswordValid(): boolean {
    return (
      (localStorage.getItem('is_password_valid') || 'true') === 'true'
    );
  }

  public get httpHeader() {
    if (
      localStorage.getItem('JWTKey') &&
      (this.router.url.includes('/approval/') ||
        this.router.url.includes('upload-evidences'))
    ) {
      return { Authorization: localStorage.getItem('JWTKey') };
    }
    return {
      Authorization: `Bearer ${localStorage.getItem('currentUser')}`,
    };
  }

  login(
    username: string,
    password: string,
    impersonate?: string | number | null,
  ): Observable<User> {
    const query = impersonate
      ? `?__impersonate=${encodeURIComponent(String(impersonate))}`
      : '';
    return this.http
      .post<User>(`/api/token-auth/${query}`, {
        username,
        password,
      })
      .pipe(
        mergeMap(async (user) => {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem(
            'currentUser',
            JSON.stringify(user.access),
          );
          if (user.session_name) {
            sessionStorage.setItem('session_name', user.session_name);
          }
          if (
            user.notification_password_expired.alert &&
            parseInt(
              user.notification_password_expired.days_before_expire,
              10,
            ) >= 0
          ) {
            this.spinner.hide();
            const modalRef = this.modalService.open(
              PasswordExpiredModalComponent,
              {
                backdrop: 'static',
                centered: true,
              },
            );
            modalRef.componentInstance.data =
              user.notification_password_expired;
            await modalRef.result;
          }

          this.currentUserSubject.next(user);
          return user;
        }),
      );
  }

  logout() {
    return this.http.post('/api/logout/', {
      refresh: localStorage.getItem('refresh'),
      session_name: sessionStorage.getItem('session_name'),
    });
  }

  getPersonDetail(id: number): Observable<UserDetail> {
    return this.http.get<UserDetail>(API_URL.people + id + '/');
  }

  forgotPassword(data: {
    email: string;
    recaptcha_response: string;
  }): Observable<any> {
    return this.http.post(API_URL.reset_password, data);
  }

  initialData(data) {
    return this.http.patch(API_URL.initial, data);
  }

  validateResetPasswordToken(data) {
    return this.http.post(API_URL.reset_password_validate, data);
  }

  resetPassword(data) {
    return this.http.post(API_URL.reset_password_confirm, data);
  }

  getPasswordPolicy() {
    return this.http.get('/api/pw-policy/');
  }
}
